import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Search, Edit3, Trash2, X, Save, Camera, Upload } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { files as filesApi } from '../services/api';

/**
 * Reusable admin CRUD panel driven by a `schema` config.
 *
 * schema: {
 *   title: string,                     // heading label
 *   testid: string,                    // data-testid prefix
 *   fields: [                          // form + list config
 *     { key, label, type, placeholder?, rows?, optionsFn?, span? }
 *   ],
 *   renderRow(item, actions): JSX,     // table row body
 *   emptyIcon: LucideIcon,
 *   emptyMessage: string,
 *   api: { list, create, update, remove },
 * }
 */
export default function AdminContentPanel({ schema }) {
  const { title, testid, fields, renderRow, emptyIcon: EmptyIcon, emptyMessage, api } = schema;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' or item
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // per-row action

  const blank = () => Object.fromEntries(fields.map(f => [f.key, f.type === 'number' ? 0 : '']));

  const load = async (query = '') => {
    setLoading(true);
    try {
      const data = await api.list(query);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(''); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(blank()); setEditing('new'); };
  const openEdit = (item) => {
    // Only carry over keys that appear in the schema so admins don't accidentally
    // rewrite system-managed fields (createdAt, ids, etc.).
    const trimmed = Object.fromEntries(fields.map(f => [f.key, item[f.key] ?? (f.type === 'number' ? 0 : '')]));
    setForm(trimmed);
    setEditing(item);
  };
  const close = () => { setEditing(null); setForm({}); };
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    // Basic required-field check (schema fields with `required: true`)
    for (const f of fields) {
      if (f.required && !form[f.key]) {
        toast({ title: `${f.label} is required` });
        return;
      }
    }
    // Coerce number fields
    const payload = { ...form };
    for (const f of fields) {
      if (f.type === 'number' && payload[f.key] !== '' && payload[f.key] != null) {
        payload[f.key] = Number(payload[f.key]);
      }
      if (f.type === 'csv' && typeof payload[f.key] === 'string') {
        payload[f.key] = payload[f.key].split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await api.create(payload);
        setRows(prev => [created, ...prev]);
        toast({ title: `${title.slice(0, -1)} created` });
      } else {
        const updated = await api.update(editing.id, payload);
        setRows(prev => prev.map(r => (r.id === updated.id ? updated : r)));
        toast({ title: 'Saved' });
      }
      close();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    const label = item.title || item.name || 'this item';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    setBusy(item.id);
    try {
      await api.remove(item.id);
      setRows(prev => prev.filter(r => r.id !== item.id));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const rowActions = (item) => (
    <div className="inline-flex gap-1 justify-end">
      <button data-testid={`${testid}-edit-${item.id}`} onClick={() => openEdit(item)}
        className="px-2 py-1 rounded-md border border-green-600/40 text-green-400 hover:bg-green-600/10 text-xs inline-flex items-center gap-1">
        <Edit3 size={11} /> Edit
      </button>
      <button data-testid={`${testid}-delete-${item.id}`} onClick={() => remove(item)} disabled={busy === item.id}
        className="px-2 py-1 rounded-md border border-red-600/40 text-red-400 hover:bg-red-600/10 text-xs inline-flex items-center gap-1 disabled:opacity-60">
        {busy === item.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Delete
      </button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid={`${testid}-panel`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute top-3 left-3 text-gray-500" />
          <input
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(q); }}
            placeholder={`Search ${title.toLowerCase()}…`}
            className="w-full bg-black border border-[#1f1f1f] rounded-lg pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <button data-testid={`${testid}-refresh`} onClick={() => load(q)}
          className="px-3 py-2 rounded-lg bg-[#1a2a20] border border-[#153b23] hover:border-green-500/40 text-xs">
          Refresh
        </button>
        <button data-testid={`${testid}-new`} onClick={openNew}
          className="pbb-btn-green text-xs inline-flex items-center gap-1">
          <Plus size={14} /> New {title.slice(0, -1)}
        </button>
        <div className="text-xs text-gray-500 ml-auto">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <EmptyIcon className="mx-auto text-green-400" size={26} />
          <div className="mt-3 font-semibold">{emptyMessage}</div>
          <p className="text-sm text-gray-400 mt-1">Click "New {title.slice(0, -1)}" to add one.</p>
        </div>
      ) : (
        <div className="pbb-card overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {rows.map(item => (
                <tr key={item.id} data-testid={`${testid}-row-${item.id}`} className="border-t border-[#1f1f1f] first:border-t-0">
                  {renderRow(item, rowActions)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          data-testid={`${testid}-form-modal`}
        >
          <div className="pbb-card w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#153b23] shrink-0">
              <h2 className="font-semibold text-white">
                {editing === 'new' ? `New ${title.slice(0, -1)}` : `Edit ${title.slice(0, -1)}`}
              </h2>
              <button onClick={close} className="p-1 rounded-md hover:bg-[#0d1f14] text-gray-400 hover:text-white" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map(f => (
                <div key={f.key} className={f.span === 2 ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs text-gray-400 mb-1">
                    {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                  </label>
                  {f.type === 'textarea' ? (
                    <textarea
                      data-testid={`${testid}-field-${f.key}`}
                      value={form[f.key] ?? ''}
                      onChange={(e) => setF(f.key, e.target.value)}
                      rows={f.rows || 3}
                      placeholder={f.placeholder || ''}
                      className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
                    />
                  ) : f.type === 'imageUpload' ? (
                    <ImageUploadField
                      testId={`${testid}-field-${f.key}`}
                      value={form[f.key]}
                      onChange={(v) => setF(f.key, v)}
                      bucket={f.bucket || 'business'}
                      aspect={f.aspect || 'video'}
                    />
                  ) : (
                    <input
                      data-testid={`${testid}-field-${f.key}`}
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={Array.isArray(form[f.key]) ? form[f.key].join(', ') : (form[f.key] ?? '')}
                      onChange={(e) => setF(f.key, e.target.value)}
                      placeholder={f.placeholder || ''}
                      className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm"
                    />
                  )}
                  {f.hint && <div className="text-[10px] text-gray-500 mt-1">{f.hint}</div>}
                  {f.type === 'url' && form[f.key] && (
                    <img src={filesApi.urlFor(form[f.key])} alt="preview" onError={(e) => (e.target.style.display = 'none')}
                      className="mt-2 h-16 rounded-md object-cover" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-[#153b23] shrink-0">
              <button onClick={close} className="px-3 py-2 rounded-lg border border-[#1f1f1f] text-gray-300 text-sm">Cancel</button>
              <button data-testid={`${testid}-save`} onClick={save} disabled={saving}
                className="pbb-btn-green text-sm inline-flex items-center gap-1 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * ImageUploadField — direct file-picker button that uploads to Emergent Object
 * Storage via `filesApi.upload(file, bucket)` and stores the returned URL in
 * the parent form. Renders a live preview at the same 16:9 aspect ratio the
 * app uses everywhere so admins/owners see how the image will look on cards.
 */
function ImageUploadField({ testId, value, onChange, bucket = 'business', aspect = 'video' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please choose an image file (JPG, PNG, WebP, GIF).' });
      e.target.value = '';
      return;
    }
    // Client-side sanity cap; backend also enforces a limit.
    const maxMB = 12;
    if (file.size > maxMB * 1024 * 1024) {
      toast({ title: `Image too large — max ${maxMB} MB.` });
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const res = await filesApi.upload(file, bucket);
      onChange(res.url);
      toast({ title: 'Image uploaded' });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const description = typeof raw === 'string' ? raw : (raw?.message || 'Try again');
      toast({ title: 'Upload failed', description });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const clear = () => onChange('');
  const preview = value ? filesApi.urlFor(value) : null;
  const ratio = aspect === 'square' ? 'aspect-square' : 'aspect-video';

  return (
    <div data-testid={testId} className="space-y-2">
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handle} data-testid={`${testId}-input`} />
      {preview ? (
        <div className="relative">
          <div className={`relative w-full ${ratio} rounded-lg overflow-hidden border border-[#1f1f1f] bg-black`}>
            <img
              src={preview}
              alt="event cover preview"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button" onClick={pick} disabled={uploading}
              data-testid={`${testId}-replace`}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-green-600/40 text-green-300 hover:bg-green-600/10 disabled:opacity-60">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Replace
            </button>
            <button
              type="button" onClick={clear} disabled={uploading}
              data-testid={`${testId}-remove`}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-600/40 text-red-300 hover:bg-red-600/10 disabled:opacity-60">
              <X size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button" onClick={pick} disabled={uploading}
          data-testid={`${testId}-picker`}
          className={`group relative w-full ${ratio} rounded-lg border-2 border-dashed border-[#2a2a2a] hover:border-green-500 bg-[#0d1f14]/40 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-green-300 transition disabled:opacity-60`}>
          {uploading ? <Loader2 size={22} className="animate-spin text-green-400" /> : <Camera size={22} />}
          <span className="text-xs font-medium">{uploading ? 'Uploading…' : 'Click to upload photo'}</span>
          <span className="text-[10px] text-gray-500">JPG, PNG or WebP · up to 12 MB</span>
        </button>
      )}
    </div>
  );
}
